import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { cn } from "@/lib/utils";

import type { ReactNode } from "react";

/**
 * Button className for the solid (white) primary action on a coloured banner
 * surface. Use with the default `<Button>` variant.
 */
export const solidPrimaryButtonClass =
  "bg-white text-foreground shadow-xs hover:bg-white/90";

/**
 * Button className for a secondary, translucent action on a coloured banner
 * surface. Use with the default `<Button>` variant.
 */
export const solidGhostButtonClass =
  "border border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white";

type Tone = "attention" | "destructive";

/** Surface colours, aligned with the payment-card badges. */
const toneSurface: Record<Tone, string> = {
  // Matches the `attention` badge (Badge variant="attention").
  attention: "bg-slate-600 text-white",
  // Matches the `destructive` badge (Badge variant="destructive").
  destructive: "bg-destructive text-destructive-foreground",
};

interface SolidNoticeBannerProps {
  /** Colour family, aligned with the payment-card badges. */
  tone: Tone;
  /** Leading icon, rendered in the banner's foreground colour. */
  icon: ReactNode;
  /** Primary, single-line message. */
  title: ReactNode;
  /** Optional muted second line (e.g. a payout failure reason). */
  description?: ReactNode;
  /** Trailing action(s): right-aligned on desktop, full-width on mobile. */
  actions?: ReactNode;
  className?: string;
  role?: string;
  "aria-label"?: string;
  "data-test-id"?: string;
}

/**
 * A notice banner with a solid colour surface, a leading icon, one line of copy
 * (plus an optional muted detail line) and trailing actions.
 *
 * Built on the shadcn `Item` primitive: media → content → actions sit on a
 * single row on desktop; on mobile the actions wrap onto a full-width second
 * row so they stay comfortably tappable.
 */
export const Banner = ({
  tone,
  icon,
  title,
  description,
  actions,
  className,
  role,
  "aria-label": ariaLabel,
  "data-test-id": dataTestId,
}: SolidNoticeBannerProps) => (
  <Item
    role={role}
    aria-label={ariaLabel}
    data-test-id={dataTestId}
    className={cn("rounded-2xl", toneSurface[tone], className)}
  >
    <ItemMedia className="[&_svg]:size-5">{icon}</ItemMedia>
    <ItemContent className="min-w-0">
      <ItemTitle className="font-semibold">{title}</ItemTitle>
      {description && (
        <ItemDescription className="text-current opacity-90">
          {description}
        </ItemDescription>
      )}
    </ItemContent>
    {actions && (
      <ItemActions className="w-full sm:w-auto *:flex-1 sm:*:flex-none">
        {actions}
      </ItemActions>
    )}
  </Item>
);
